import type { PurchaseOrder } from "@/features/purchase-orders";

// BLU-574: all in-flight purchase-order work. This is shared by the buyer and
// partner lists so their default "Active" views cannot drift apart.
export const ACTIVE_PURCHASE_ORDER_STATUSES: PurchaseOrder["status"][] = [
  "draft",
  "submitted",
  "in_production",
  "ready",
  "in_transit",
  "partially_received",
];

export const isActivePurchaseOrderStatus = (status: string | null): boolean =>
  status !== null && ACTIVE_PURCHASE_ORDER_STATUSES.some((activeStatus) => activeStatus === status);

/** Sum of ordered vs received quantities across an order's line items —
 *  shared by OrdersList and TransfersList to drive the status-select guard
 *  and the valid-status computation below. */
export const getOrderReceiptTotals = (order: PurchaseOrder) => {
  const totalOrdered = (order.items ?? []).reduce((sum, item) => sum + (item.ordered_quantity ?? 0), 0);
  const totalReceived = (order.items ?? []).reduce((sum, item) => sum + (item.received_quantity ?? 0), 0);
  return { totalOrdered, totalReceived };
};

/** Status options valid for a list-row status-select, given the order's
 *  current receipt totals. `updateOptions` is the type-specific status
 *  chain — pass STATUS_UPDATE_OPTIONS for purchase orders or
 *  TRANSFER_STATUS_UPDATE_OPTIONS for transfers. Always includes the
 *  order's current status even if it falls outside the computed set, so
 *  the select never renders without its own value selected. */
export const getValidStatusesForOrder = (
  order: PurchaseOrder,
  updateOptions: PurchaseOrder["status"][]
): PurchaseOrder["status"][] => {
  const { totalOrdered, totalReceived } = getOrderReceiptTotals(order);
  const filtered = updateOptions.filter((status) => {
    if (status === "partially_received") {
      return totalReceived > 0 && totalReceived < totalOrdered;
    }
    if (status === "received") {
      return totalOrdered > 0 && totalReceived >= totalOrdered;
    }
    return true;
  });
  if (!filtered.includes(order.status)) {
    filtered.unshift(order.status);
  }
  return filtered;
};

/** Compact uppercase label for the list-row status pill/select — distinct
 *  from STATUS_LABELS in status-style.ts (title case, used in the PoActionBar
 *  / SubmitOrderTab page-header pills). Different UI density, kept separate
 *  deliberately. */
export const formatStatusLabel = (status: PurchaseOrder["status"]) =>
  status === "partially_received"
    ? "PARTIAL"
    : status === "in_transit"
      ? "IN TRANSIT"
      : status.replace("_", " ").toUpperCase();

export const getShipmentStatusClassName = (status: string | null | undefined) => {
  switch (status) {
    case "pending":
      return "bg-slate-100 text-slate-700 hover:bg-slate-100";
    case "in_transit":
      return "bg-blue-100 text-blue-800 hover:bg-blue-100";
    case "delivered":
      return "bg-emerald-100 text-emerald-800 hover:bg-emerald-100";
    case "cancelled":
      return "bg-red-100 text-red-800 hover:bg-red-100";
    default:
      return "bg-slate-100 text-slate-700 hover:bg-slate-100";
  }
};

export type StatusChangeGuardResult =
  | { valid: true }
  | { valid: false; title: string; description: string };

/** Pure guard for the list-row status-select onChange — decides whether a
 *  transition is legal given the order's current receipt totals. The caller
 *  owns the toast + navigate + mutation call (those are page-specific: the
 *  redirect path differs between /orders and /transfers). */
export const validateGuardedStatusChange = (
  order: PurchaseOrder,
  newStatus: PurchaseOrder["status"]
): StatusChangeGuardResult => {
  const { totalOrdered, totalReceived } = getOrderReceiptTotals(order);

  if (newStatus === "partially_received" && !(totalReceived > 0 && totalReceived < totalOrdered)) {
    return {
      valid: false,
      title: "Invalid partial status",
      description: "Partially Received is only valid when some, but not all, units are received.",
    };
  }

  if (newStatus === "received" && !(totalOrdered > 0 && totalReceived >= totalOrdered)) {
    return {
      valid: false,
      title: "Invalid received status",
      description: "Received is only valid when all ordered units are received.",
    };
  }

  return { valid: true };
};
